/**
 * 8.4 — Comportement des comptes « un seul jour ».
 *
 * Ce sont les vrais abandons : un seul jour d'activité, et entrés au moins sept
 * jours avant la fin des données — sans cette seconde condition, tout compte
 * inscrit hier serait compté comme perdu.
 *
 * L'intérêt de cette analyse est la question qu'elle permet de poser : ces
 * comptes ont-ils VU le produit ? Un compte qui n'a jamais lancé un
 * apprentissage n'a pas abandonné le produit, il ne l'a pas essayé. Et un
 * compte qui n'a fait qu'UNE action est souvent un échec technique, pas un
 * désintérêt.
 */

import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatDuration, formatInteger, formatPercent } from '../../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS, SEQUENTIAL_RAMP } from '../../lib/theme';
import type { ChurnAnalysis, DataColumn, LabelledBucket } from '../../types';
import { ChartCard } from '../Card';
import { ReadingBox } from '../ReadingBox';
import { StatTile } from '../StatTile';
import { ChartTooltip } from '../ChartTooltip';

const bucketColumns: ReadonlyArray<DataColumn<LabelledBucket>> = [
  { key: 'label', header: 'Actions', render: (row) => row.label },
  { key: 'users', header: 'Comptes', numeric: true, render: (row) => formatInteger(row.users) },
];

interface SimpleRow {
  label: string;
  users: number;
  share: number;
}

const simpleColumns: ReadonlyArray<DataColumn<SimpleRow>> = [
  { key: 'label', header: 'Première action', render: (row) => row.label },
  { key: 'users', header: 'Comptes', numeric: true, render: (row) => formatInteger(row.users) },
  { key: 'share', header: 'Part', numeric: true, render: (row) => formatPercent(row.share) },
];

export function ChurnPanel({ data }: { data: ChurnAnalysis }) {
  const neverLearnShare = data.users > 0 ? (data.neverTriedLearn / data.users) * 100 : 0;

  const learnSplit: SimpleRow[] = [
    {
      label: 'A essayé un apprentissage',
      users: data.triedLearn,
      share: data.users > 0 ? (data.triedLearn / data.users) * 100 : 0,
    },
    { label: "N'a jamais essayé", users: data.neverTriedLearn, share: neverLearnShare },
  ];

  const firstActions: SimpleRow[] = data.firstActions.map((item) => ({
    label: item.actionName,
    users: item.users,
    share: item.share,
  }));

  return (
    <>
      <ReadingBox
        method={
          <>
            Comptes ayant exactement <strong>un</strong> jour d'activité distinct, et entrés au
            moins sept jours avant la fin des données disponibles. Sans cette seconde condition,
            tout compte inscrit récemment serait compté comme perdu. La durée de la session
            unique est l'écart entre sa première et sa dernière action : un compte à une seule
            action mesure donc 0 seconde.
          </>
        }
      >
        {data.users === 0
          ? 'Aucun compte ne remplit les conditions sur cette période.'
          : neverLearnShare > 50
            ? `${formatPercent(neverLearnShare)} de ces comptes n'ont jamais lancé un apprentissage. Ils n'ont pas abandonné le produit : ils ne l'ont pas essayé.`
            : `${formatPercent(100 - neverLearnShare)} de ces comptes ont bien essayé un apprentissage avant de ne jamais revenir — le problème n'est pas la découverte.`}
        {data.singleActionShare > 15
          ? ` Et ${formatPercent(data.singleActionShare)} n'ont produit qu'une seule action, ce qui ressemble davantage à un échec technique qu'à un désintérêt.`
          : ''}
      </ReadingBox>

      <div className="kpi-row">
        <StatTile
          label="Comptes concernés"
          value={formatInteger(data.users)}
          hint={`${formatPercent(data.shareOfCohort)} de la cohorte jugeable`}
        />
        <StatTile
          label="Sans apprentissage"
          value={formatPercent(neverLearnShare)}
          hint="n'ont jamais lancé le mode Learn"
        />
        <StatTile
          label="Une seule action"
          value={formatPercent(data.singleActionShare)}
          hint="piste d'échec technique"
        />
        <StatTile
          label="Actions médianes"
          value={formatInteger(data.medianActions)}
          hint="dans la session unique"
        />
        <StatTile
          label="Durée médiane"
          value={formatDuration(data.medianSessionSeconds)}
          hint="de la session unique"
        />
      </div>

      <div className="chart-grid">
        <ChartCard
          title="Ont-ils essayé le mode apprentissage ?"
          subtitle="Deux catégories exclusives, donc une seule teinte"
          rows={learnSplit}
          columns={simpleColumns}
          chartHeight={200}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={learnSplit} layout="vertical" margin={{ top: 4, right: 64, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={COLORS.grid} horizontal={false} />
              <XAxis type="number" tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fill: COLORS.textSecondary, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={185}
              />
              <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={<ChartTooltip valueFormatter={formatInteger} />} />
              <Bar isAnimationActive={false} dataKey="users" name="Comptes" fill={COLORS.series1} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE}>
                <LabelList
                  dataKey="share"
                  position="right"
                  fill={COLORS.textSecondary}
                  fontSize={12}
                  formatter={(value: unknown) => formatPercent(Number(value))}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Première action"
          subtitle="Par quoi ces comptes ont commencé"
          rows={firstActions}
          columns={simpleColumns}
          chartHeight={Math.max(200, firstActions.length * 34 + 40)}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={firstActions} layout="vertical" margin={{ top: 4, right: 64, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={COLORS.grid} horizontal={false} />
              <XAxis type="number" tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
                axisLine={false}
                tickLine={false}
                width={130}
              />
              <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={<ChartTooltip valueFormatter={formatInteger} />} />
              <Bar isAnimationActive={false} dataKey="users" name="Comptes" fill={COLORS.series1} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE}>
                <LabelList
                  dataKey="share"
                  position="right"
                  fill={COLORS.textSecondary}
                  fontSize={12}
                  formatter={(value: unknown) => formatPercent(Number(value))}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="chart-grid__wide">
          <ChartCard
            title="Actions dans la session unique"
            subtitle="Seaux ordonnés — la couleur redouble l'ordre"
            rows={data.actionsHistogram}
            columns={bucketColumns}
            chartHeight={260}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.actionsHistogram as LabelledBucket[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: COLORS.textSecondary, fontSize: 11 }}
                  axisLine={{ stroke: COLORS.grid }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }} axisLine={false} tickLine={false} width={44} />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={<ChartTooltip valueFormatter={formatInteger} />} />
                <Bar isAnimationActive={false} dataKey="users" name="Comptes" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE * 2}>
                  {data.actionsHistogram.map((bucket, index) => (
                    <Cell key={bucket.label} fill={SEQUENTIAL_RAMP[Math.min(index, SEQUENTIAL_RAMP.length - 1)]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}
